import { faEye, faEyeSlash, faShieldAlt, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useCorporateMarketingParams } from './customerRegistrationHooks';
import { ProfitRoverLogoIcon } from '../generic/ProfitRoverLogos';
import { REGISTRATION_STEPS, useRegistrationContext } from './registrationContext';
import { emailIsValid } from './registrationUtil';
import { PasswordPolicy } from '../auth/password-policy';
import { baseProfitRoverRules } from '../auth/password-rules';
import { ProfitRoverPrimaryButton } from '../forms/ProfitRoverButtons';
import ProfitRoverTermsOfUseLink from '../generic/ProfitRoverTermsOfUseLink';
import MailToSupportLink from '../generic/MailToSupportLink';
import { useRequestRegistrationCode } from '../../data-access/mutation/stripe';
import { gaEmitSendVerificationCodeButtonClick } from '../../google-analytics/signup';
import './request-code.scss';

const TrustSignals = () => (
  <div className="trust-signals">
    <div className="trust-signal">
      <FontAwesomeIcon icon={faCheck} className="trust-icon" />
      <span>Full access to all features</span>
    </div>
    <div className="trust-signal">
      <FontAwesomeIcon icon={faCheck} className="trust-icon" />
      <span>No credit card required</span>
    </div>
    <div className="trust-signal">
      <FontAwesomeIcon icon={faCheck} className="trust-icon" />
      <span>Instant setup</span>
    </div>
  </div>
);

const generateDescriptionByRuleId = id => {
  switch (id) {
    case 'minlength':
      return '8+ characters';
    case 'lowercase':
      return 'Lowercase letter';
    case 'uppercase':
      return 'Uppercase letter';
    case 'digit':
      return 'Number';
    case 'special':
      return 'Special character (!@#$%&*?)';
    case 'forbidden':
      return 'No common words';
    default:
      return '';
  }
};

const PasswordRequirements = ({ rules, show, allSatisfied }) => {
  if (!show) {
    return null;
  }

  // If all rules are satisfied, show a simple success message
  if (allSatisfied) {
    return (
      <div className="password-requirements-success">
        <FontAwesomeIcon icon={faCheck} className="success-icon" />
        <span>Password meets all requirements</span>
      </div>
    );
  }

  // Show unsatisfied rules
  const unsatisfiedRules = rules.filter(rule => !rule.isSatisfied);

  return (
    <div className="password-requirements-inline">
      <span className="requirements-label">Needs:</span>
      {unsatisfiedRules.map((rule, index) => (
        <span key={rule.id} className="requirement-tag">
          {generateDescriptionByRuleId(rule.id)}
          {index < unsatisfiedRules.length - 1 && ', '}
        </span>
      ))}
    </div>
  );
};

const usePasswordVisibility = () => {
  const [passwordIsVisible, setPasswordIsVisible] = React.useState(false);

  const togglePasswordVisibility = React.useCallback(() => setPasswordIsVisible(!passwordIsVisible), [
    passwordIsVisible,
  ]);

  return [passwordIsVisible, togglePasswordVisibility];
};

const GatherAccountDetails = () => {
  const { setStep, emailAddress, setEmailAddress, password, setPassword } = useRegistrationContext();

  const requestCodeMutation = useRequestRegistrationCode();

  const [passwordIsAllowed, setPasswordIsAllowed] = React.useState(false);
  const [agreeToTermsAndPrivacy, setAgreeToTermsAndPrivacy] = React.useState(false);
  const [passwordIsVisible, togglePasswordVisibility] = usePasswordVisibility();
  const [activeInputField, setActiveInputField] = React.useState(null);
  const [errorRequestingCode, setErrorRequestingCode] = React.useState(false);
  const [initialPasswordFocus, setInitialPasswordFocus] = React.useState(false);
  const [hasStartedTypingPassword, setHasStartedTypingPassword] = React.useState(false);

  const passwordPolicy = React.useMemo(() => new PasswordPolicy(baseProfitRoverRules), []);

  const passwordChangeHandler = inputPassword => {
    const isValid = passwordPolicy.checkPassword(inputPassword);
    setPasswordIsAllowed(isValid);
  };

  const { emailParam: incomingEmailParam } = useCorporateMarketingParams();
  const incomingEmailParamIsSpecified = incomingEmailParam != null && incomingEmailParam !== '';
  const incomingEmailParamIsNotSpecified = !incomingEmailParamIsSpecified;

  const emailInputRef = React.useCallback(
    emailInput => {
      if (emailInput && (incomingEmailParamIsNotSpecified || errorRequestingCode)) {
        emailInput.focus();
      }
    },
    [incomingEmailParamIsNotSpecified, errorRequestingCode],
  );

  const passwordInputRef = React.useCallback(
    passwordInput => {
      if (emailAddress == null || password === '') {
        passwordPolicy.resetRules();
      }
      if (passwordInput && !initialPasswordFocus && incomingEmailParamIsSpecified) {
        passwordInput.focus();
        setInitialPasswordFocus(true);
      }
    },
    [emailAddress, password, initialPasswordFocus, incomingEmailParamIsSpecified, passwordPolicy],
  );

  const enableNextStepButton = passwordIsAllowed && agreeToTermsAndPrivacy && emailIsValid(emailAddress);

  const submitFormButton = async () => {
    gaEmitSendVerificationCodeButtonClick();

    try {
      await requestCodeMutation.mutateAsync({ emailAddress, password });
      setStep(REGISTRATION_STEPS.REDEEM_CODE);
    } catch (error) {
      setErrorRequestingCode(true);
    }
  };

  return (
    <div className="d-flex flex-column align-items-center">
      <div className="request-code-redesign">
        <div className="registration-layout">
          {/* Left side - Trust signals */}
          <div className="trust-panel">
            <h2 className="trust-headline">Start maximizing your profits in 60 seconds</h2>
            <TrustSignals />
            <p className="social-proof">Joined by 5,000+ profit-driven businesses</p>
          </div>

          {/* Right side - Form */}
          <div className="form-panel">
            <div className="create-account-container">
              <div className="form-header">
                <ProfitRoverLogoIcon className="form-logo" />
                <p className="title">Create Your Account</p>
              </div>

              <form>
                <label className="input-field" htmlFor="user-registration-email">
                  {activeInputField === 'user-registration-email' && (
                    <span className="input-field-dynamic-label">Email</span>
                  )}
                  <input
                    type="email"
                    id="user-registration-email"
                    value={emailAddress}
                    onChange={e => {
                      setEmailAddress(e.target.value);
                      setErrorRequestingCode(false);
                    }}
                    onFocus={e => setActiveInputField(e.target.id)}
                    onBlur={() => setActiveInputField(null)}
                    placeholder="Email"
                    ref={emailInputRef}
                  />
                </label>

                <label className="input-field" htmlFor="user-registration-password">
                  {activeInputField === 'user-registration-password' && (
                    <span className="input-field-dynamic-label">Password (8+ characters)</span>
                  )}
                  <input
                    type={passwordIsVisible ? 'text' : 'password'}
                    id="user-registration-password"
                    value={password}
                    onChange={e => {
                      setPassword(e.target.value);
                      setHasStartedTypingPassword(true);
                      passwordChangeHandler(e.target.value);
                    }}
                    onFocus={e => setActiveInputField(e.target.id)}
                    onBlur={() => setActiveInputField(null)}
                    placeholder="Password (8+ characters)"
                    ref={passwordInputRef}
                  />
                  <FontAwesomeIcon
                    className="password-visibility-icon"
                    onClick={togglePasswordVisibility}
                    icon={passwordIsVisible ? faEye : faEyeSlash}
                  />
                </label>

                <PasswordRequirements
                  rules={passwordPolicy.rules}
                  show={hasStartedTypingPassword && password.length > 0}
                  allSatisfied={passwordIsAllowed}
                />

                <label
                  htmlFor="agree"
                  className="agree-checkbox-container"
                  aria-label="Agree to Terms and Privacy Policy"
                >
                  <input
                    type="checkbox"
                    id="agree"
                    onChange={() => setAgreeToTermsAndPrivacy(!agreeToTermsAndPrivacy)}
                  />
                  <span className="checkmark" />
                  <div>
                    I agree to the ProfitRover&nbsp;
                    <ProfitRoverTermsOfUseLink>Terms</ProfitRoverTermsOfUseLink>
                    &nbsp;&amp;&nbsp;
                    <a href="https://profitrover.com/privacy/" target="_blank" rel="noreferrer">
                      Privacy Policy
                    </a>
                  </div>
                </label>
              </form>

              <ProfitRoverPrimaryButton
                className="start-trial-button"
                disabled={!enableNextStepButton}
                onClick={submitFormButton}
              >
                Start My Free Trial
              </ProfitRoverPrimaryButton>

              <div className="button-micro-caption">
                <FontAwesomeIcon icon={faShieldAlt} className="shield-icon" />
                <span>No commitment. Cancel anytime.</span>
              </div>

              {errorRequestingCode && (
                <div className="error-text">
                  An error occurred. Please try again or
                  <MailToSupportLink
                    subject="Registration Assistance Required"
                    body="I am having trouble creating an account."
                  >
                    &nbsp;contact support
                  </MailToSupportLink>
                  .
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GatherAccountDetails;
